import { makeAutoObservable } from "mobx";
import {
  DraftCalendarEvent,
  DraftEntry,
  DraftSource,
  MutationDraft,
  MutationPlan,
  MutationRequest,
  MutationScope,
  MutationScopeKey,
  isMutationPlan,
  isFailedPlacement,
} from "./types";
import { DEFAULT_SCOPE, Scope, toScopeKey } from "../common";
import { AgentStore } from "../Agent";

// Re-export types for backward compatibility
export type { DraftCalendarEvent, DraftEventType } from "./types";
export type { AgentState, AgentStatus, AgentName } from "../Agent";

// ─── Store (single-scope, unchanged API) ─────────────────────────────────────

/**
 * Holds mutation drafts (shift+placement plans or simple placements) per
 * generic draft ID so the calendar can render them as draft overlays.
 *
 * Source-agnostic: works for scheduler agent, Dauertermin, or manual edits.
 * This store knows nothing about scoping — it manages one flat set of drafts.
 *
 * Also holds agent state (status, progress, solution, error) for the scheduler
 * agent workflow running in this scope.
 */
export class CalendarAppointmentMutationDraftStore {
  /** Active Draft entries keyed by draftId */
  entries = new Map<string, DraftEntry>();

  /** Mutation requests keyed by draftId — persists after draft removal so regeneration works */
  requests = new Map<string, MutationRequest>();

  /** Rejection descriptions keyed by draftId */
  rejections = new Map<string, string>();

  /** Agent lifecycle store */
  readonly agent = new AgentStore();

  constructor() {
    makeAutoObservable(this, { agent: false }, { autoBind: true });
  }

  setDraft(draftId: string, draft: MutationDraft, request: MutationRequest, source?: DraftSource) {
    this.requests.set(draftId, request);
    this.entries.set(draftId, {
      draft,
      source: source ?? request.source,
      request,
    });
  }

  removeDraft(draftId: string) {
    this.entries.delete(draftId);
  }

  setRejection(draftId: string, reason: string) {
    this.rejections.set(draftId, reason);
  }

  clearRejection(draftId: string) {
    this.rejections.delete(draftId);
  }

  clearAllRejections() {
    this.rejections.clear();
  }

  clearDrafts() {
    this.entries.clear();
  }

  clearAll() {
    this.entries.clear();
    this.requests.clear();
    this.rejections.clear();
    this.agent.reset();
  }

  // ── Queries ──

  getDraft(draftId: string): MutationDraft | undefined {
    return this.entries.get(draftId)?.draft;
  }

  getEntry(draftId: string): DraftEntry | undefined {
    return this.entries.get(draftId);
  }

  getRequest(draftId: string): MutationRequest | undefined {
    return this.requests.get(draftId);
  }

  getRejection(draftId: string): string | undefined {
    return this.rejections.get(draftId);
  }

  get hasDrafts(): boolean {
    return this.entries.size > 0;
  }

  /** All known request IDs (includes rejected — everything the store has seen). */
  get requestIds(): string[] {
    return [...this.requests.keys()];
  }

  /** All stored MutationRequests (for rendering card lists from the store). */
  get allRequests(): MutationRequest[] {
    return [...this.requests.values()];
  }

  /** IDs of drafts that have a non-empty rejection reason. */
  get rejectedIds(): string[] {
    return [...this.rejections.entries()]
      .filter(([, reason]) => reason.trim().length > 0)
      .map(([id]) => id);
  }

  get hasRejections(): boolean {
    return this.rejectedIds.length > 0;
  }

  /** Type guard — convenience static for consumers. */
  static isMutationPlan(d: MutationDraft): d is MutationPlan {
    return isMutationPlan(d);
  }

  /**
   * Flat list of draft calendar events derived from all stored drafts.
   * The calendar can iterate this to render coloured overlays.
   */
  get draftEvents(): DraftCalendarEvent[] {
    const events: DraftCalendarEvent[] = [];

    for (const [draftId, entry] of this.entries) {
      const { draft } = entry;

      // Skip failed placements — they have no calendar representation
      if (isFailedPlacement(draft)) continue;

      for (const sh of draft.shifts) {
        events.push({
          id: `shift-${sh.timeSlotId}`,
          type: "shift",
          draftId,
          therapistId: sh.newTherapistId,
          therapistName: sh.newTherapistName,
          start: sh.newStart,
          end: sh.newEnd,
          label: sh.label,
          originalStart: sh.originalStart,
          originalEnd: sh.originalEnd,
          originalTherapistId: sh.originalTherapistId,
          originalTherapistName: sh.originalTherapistName,
          appointmentId: sh.appointmentId,
          timeSlotId: sh.timeSlotId,
        });
      }
      for (let i = 0; i < draft.placements.length; i++) {
        const p = draft.placements[i];
        events.push({
          id: `placement-${draftId}-${p.timeSlotId || p.appointmentId || `${i}-${p.start}-${p.end}`}`,
          type: "placement",
          draftId,
          therapistId: p.therapistId,
          therapistName: p.therapistName,
          start: p.start,
          end: p.end,
          label: "Neue Platzierung",
          appointmentId: p.appointmentId,
          timeSlotId: p.timeSlotId,
        });
      }

      // Driving-time events
      const drivingTimes = draft.drivingTimes ?? [];
      for (const dt of drivingTimes) {
        if (!dt.start || !dt.end) continue; // delete-only entries have no time range
        const actionLabel =
          dt.action === "create"
            ? "🚗+ Fahrzeit"
            : dt.action === "delete"
              ? "🚗− Fahrzeit"
              : "🚗~ Fahrzeit";
        events.push({
          id: `driving-${dt.id ?? `${draftId}-${dt.therapistId}-${dt.start}`}`,
          type: "driving-time",
          draftId,
          therapistId: dt.therapistId,
          therapistName: dt.therapistName,
          start: dt.start,
          end: dt.end,
          label: actionLabel,
          drivingTimeAction: dt.action,
        });
      }
    }

    return events;
  }
}

// ─── Registry (scope → store factory) ────────────────────────────────────────

/**
 * Manages one CalendarAppointmentMutationDraftStore per (patientId, locationId)
 * scope.  This is the single provider-level instance; hooks resolve the inner
 * store for their active scope and interact with its unchanged API.
 *
 * Cross-scope operations (e.g. invalidation when the shared calendar changes)
 * are available via `getStoresByLocation`.
 */
export class ScopedCalendarAppointmentMutationDraftStoreRegistry {
  /** scope-key → store instance */
  stores = new Map<MutationScopeKey, CalendarAppointmentMutationDraftStore>();

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /** Get or create the store for a scope. */
  getStore(scope: MutationScope): CalendarAppointmentMutationDraftStore {
    const key = toScopeKey(scope);
    const existing = this.stores.get(key);
    if (existing) return existing;

    const store = new CalendarAppointmentMutationDraftStore();
    this.stores.set(key, store);
    return store;
  }

  /** Get or create the shared fallback store when no explicit scope is provided. */
  getDefaultStore(): CalendarAppointmentMutationDraftStore {
    return this.getStore(DEFAULT_SCOPE as unknown as MutationScope);
  }

  /** Remove and dispose a scope (e.g. patient tab closed). */
  clearScope(scope: MutationScope) {
    const key = toScopeKey(scope);
    const store = this.stores.get(key);
    if (store) {
      store.clearAll();
      this.stores.delete(key);
    }
  }

  /** Clear and remove the fallback default scope store. */
  clearDefaultScope() {
    this.clearScope(DEFAULT_SCOPE as unknown as MutationScope);
  }

  /** Remove all scopes. */
  clearAll() {
    for (const store of this.stores.values()) {
      store.clearAll();
    }
    this.stores.clear();
  }

  /** Get all stores sharing a locationId — for cross-scope invalidation. */
  getStoresByLocation(locationId: string): CalendarAppointmentMutationDraftStore[] {
    return [...this.stores.entries()]
      .filter(([key]) => key.includes(`locationId=${locationId}`))
      .map(([, store]) => store);
  }

  /** Get all scopes sharing a locationId. */
  getScopesByLocation(locationId: string): MutationScope[] {
    return [...this.stores.entries()]
      .filter(([key]) => key.includes(`locationId=${locationId}`))
      .map(([key]) => {
        // Parse back from "locationId=X|patientId=Y"
        const parts = Object.fromEntries(key.split("|").map((p) => p.split("=")));
        return { locationId: parts.locationId, patientId: parts.patientId } as MutationScope;
      });
  }
}
